// Editing a campaign that already exists — what the form may offer, and what it
// is allowed to send.
//
// The one rule this module does NOT contain is which fields a status permits.
// That table lives on the server and arrives on every campaign DTO as
// `editableFields`. Reimplementing it here is how the screen and the dialler
// come to disagree: the form offers a template change on a RUNNING campaign,
// the server refuses it, and the person is left arguing with a 400. So every
// function below takes the server's list as an input and never invents one.
//
// Type-free on purpose: a WhatsApp campaign and a voice campaign share nothing
// but this mechanism, and the two DTOs have different field names for the same
// idea. See campaignEditRules.test.ts.

/** What a form control holds. Text and number inputs both hand back strings. */
export type FormValue = string | number | boolean | null | undefined;

/**
 * The fields the server says may be edited right now.
 *
 * Absent, null, not an array, or full of junk all mean the same thing: nothing
 * is editable. A missing list is never read as "everything" — an older server
 * that does not send the field must not hand the UI a blank cheque.
 */
export const editableFieldsOf = (campaign: any): string[] => {
  const raw = campaign?.editableFields;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((f: unknown): f is string => typeof f === 'string' && f.trim() !== '')
    .map((f: string) => f.trim());
};

/** Whether to show an edit control at all. */
export const canEdit = (campaign: any): boolean => editableFieldsOf(campaign).length > 0;

/** Whether this one field may be edited. */
export const isEditable = (campaign: any, field: string): boolean =>
  editableFieldsOf(campaign).includes(field);

/**
 * The fields this form knows how to render that the server did NOT offer.
 *
 * Order follows the form, not the server's list, so the note below reads in the
 * same order the person would have looked for the missing controls.
 */
export const lockedFields = (knownFields: string[], editable: string[]): string[] =>
  knownFields.filter(f => !editable.includes(f));

/** "a, b and c" — an Oxford-free list, because it is read as prose. */
export const joinList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/** Beyond this many, the sentence stops being a sentence. */
const NAME_LIMIT = 3;

/**
 * Why the fields that are missing from the form are missing.
 *
 * Somebody who opens Edit on a running campaign and cannot find the template
 * should not have to guess whether it is hidden, broken, or somewhere else on
 * the page. The status is quoted verbatim from the campaign, and the field list
 * is derived from what the server withheld — this sentence is a report of the
 * server's answer, not a second opinion about it.
 */
export const lockedNote = (status: string | null | undefined, lockedLabels: string[]): string | null => {
  if (lockedLabels.length === 0) return null;
  const named = lockedLabels.length > NAME_LIMIT
    ? `${joinList(lockedLabels.slice(0, NAME_LIMIT))} and ${lockedLabels.length - NAME_LIMIT} more`
    : joinList(lockedLabels);
  const verb = lockedLabels.length === 1 ? 'is' : 'are';
  const s = (status || '').trim().toUpperCase();
  return s
    ? `${named} ${verb} fixed while this campaign is ${s}.`
    : `${named} ${verb} not editable right now.`;
};

/**
 * Two form values are the same edit.
 *
 * A number input hands back "60" where the DTO held 60, and a cleared field
 * hands back "" where the DTO held null. Comparing those raw would send the
 * whole form on every save — which, on a field the status does not permit, is
 * a 400 for a change nobody made.
 */
const sameValue = (a: FormValue, b: FormValue): boolean => {
  const flat = (v: FormValue): string => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    return String(v).trim();
  };
  return flat(a) === flat(b);
};

/**
 * The body to send: only fields that are editable AND actually changed.
 *
 * `toApi` converts one form value into what the API wants (an int, an epoch, a
 * trimmed string). Returning undefined from it drops the field — a value the
 * form cannot express is not sent as null.
 */
export const buildPatch = (
  initial: Record<string, FormValue>,
  current: Record<string, FormValue>,
  editable: string[],
  toApi: (field: string, value: FormValue) => any = (_f, v) => v,
): Record<string, any> => {
  const body: Record<string, any> = {};
  editable.forEach(field => {
    if (!Object.prototype.hasOwnProperty.call(current, field)) return;
    if (sameValue(initial[field], current[field])) return;
    const value = toApi(field, current[field]);
    if (value === undefined) return;
    body[field] = value;
  });
  return body;
};

/** Nothing to send. Saving an empty body is a request that can only fail. */
export const isEmptyPatch = (body: Record<string, any>): boolean => Object.keys(body).length === 0;

// ── value conversions ───────────────────────────────────────────────────────

/** A whole number, or undefined when the box is empty or not a number. */
export const asInt = (v: FormValue): number | undefined => {
  if (typeof v === 'boolean') return undefined;
  const s = String(v ?? '').trim();
  if (s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
};

/** A trimmed string, or undefined when it is empty. */
export const asText = (v: FormValue): string | undefined => {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
};

/**
 * What `<input type="datetime-local">` wants: the instant in LOCAL time.
 *
 * `toISOString().slice(0,16)` is the standing mistake here — it is UTC, so a campaign
 * scheduled for 9am shows as 3:30am to the person who scheduled it.
 * Accepts epoch millis (WhatsApp) or an ISO string (voice).
 */
export const toDateTimeLocal = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '';
  const d = new Date(typeof v === 'number' ? v : String(v));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Epoch millis from a datetime-local box, or undefined when it is empty. */
export const epochFromLocal = (v: FormValue): number | undefined => {
  const s = asText(v);
  if (!s) return undefined;
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? undefined : t;
};

/** ISO instant from a datetime-local box, or undefined when it is empty. */
export const isoFromLocal = (v: FormValue): string | undefined => {
  const t = epochFromLocal(v);
  return t === undefined ? undefined : new Date(t).toISOString();
};

/**
 * What went wrong, in the server's own words.
 *
 * A field the current status does not accept comes back as a 400 with a
 * sentence written for a human. Swallowing it and printing "Update failed"
 * throws away the only part of the answer worth reading.
 */
export const failureMessage = (res: { error?: string; message?: string } | null | undefined,
                               fallback: string): string =>
  (res?.error || res?.message || fallback);
